import React, { useState } from "react";
import { Link } from "react-router-dom";
import SmartLink from "../../components/SmartLink";
import UpdatesTimeline from "../../components/UpdatesTimeline";
import PageBlocks from "../../components/PageBlocks";
import { getRatingScoreColor } from "../../utils/ratingColor";

const BUTTON_CLASSES = {
  twitch: "btn btn-primary btn-twitch",
  youtube: "btn btn-primary btn-youtube",
  primary: "btn btn-primary",
  secondary: "btn btn-secondary",
};

export function HomeButtons({ buttons, className = "hero-buttons" }) {
  const visible = (buttons || []).filter((button) => button.label && button.href);
  if (!visible.length) return null;
  return (
    <div className={className}>
      {visible.map((button, index) => (
        <SmartLink key={index} href={button.href} className={BUTTON_CLASSES[button.style] || BUTTON_CLASSES.primary}>
          {button.icon && <i className={button.icon} aria-hidden="true" />} {button.label}
        </SmartLink>
      ))}
    </div>
  );
}

function SectionHeading({ section, action }) {
  return (
    <div className="home-section-head">
      <div>
        {section.eyebrow && <p className="animus-hero-id font-mono">{section.eyebrow}</p>}
        <h2>
          {section.heading} {section.highlight && <span className="highlight">{section.highlight}</span>}
        </h2>
        {section.intro && <p className="home-section-intro">{section.intro}</p>}
      </div>
      {action}
    </div>
  );
}

function SectionStatus({ loading, error, empty, label }) {
  if (loading) {
    return (
      <div className="video-loading">
        <div className="spinner"></div>
        <p>Loading {label}...</p>
      </div>
    );
  }
  if (error) {
    return <div className="video-loading"><p>{error}</p></div>;
  }
  if (empty) {
    return <div className="video-loading"><p>Nothing here yet.</p></div>;
  }
  return null;
}

function ProductTag({ product }) {
  return (
    <div className={`product-tag ${product.tag}`}>
      {product.tag === "premium" ? "Premium" : product.tag.charAt(0).toUpperCase() + product.tag.slice(1)}
    </div>
  );
}

export function ToolsSection({ section, products, loading, error }) {
  const [spotlight, ...rest] = products;
  return (
    <section className="home-section home-tools">
      <div className="container">
        <SectionHeading
          section={section}
          action={<Link to="/tools" className="btn btn-secondary">All tools</Link>}
        />
        <SectionStatus loading={loading} error={error} empty={!products.length} label="tools" />
        {spotlight && (
          <article className="home-spotlight">
            <SmartLink href={spotlight.link} className="home-spotlight__media" tabIndex={-1} aria-hidden="true">
              <img src={spotlight.img} alt="" />
            </SmartLink>
            <div className="home-spotlight__body">
              <p className="home-spotlight__label font-mono">
                <i className="fas fa-bolt" aria-hidden="true" /> Spotlight
              </p>
              <h3>{spotlight.title}</h3>
              <p>{spotlight.description}</p>
              <div className="home-spotlight__pills font-mono">
                <span className={`product-tag ${spotlight.tag}`}>{spotlight.tag}</span>
                {spotlight.location && <span>{spotlight.location}</span>}
              </div>
              <SmartLink href={spotlight.link} className="btn btn-primary">
                {spotlight.btnText || "View tool"} <i className="fas fa-arrow-right" aria-hidden="true" />
              </SmartLink>
            </div>
          </article>
        )}
        {rest.length > 0 && (
          <div className="home-tool-grid">
            {rest.map((product) => (
              <SmartLink href={product.link} className="home-tool-card" key={product.id}>
                <div className="home-tool-card__image">
                  <img src={product.img} alt={product.alt} loading="lazy" />
                  {product.featured && (
                    <span className="home-tool-card__badge font-mono"><i className="fas fa-star" aria-hidden="true" /> Featured</span>
                  )}
                </div>
                <div className="home-tool-card__body">
                  <ProductTag product={product} />
                  <h3>{product.title}</h3>
                  <p>{product.description}</p>
                  <span className="home-tool-card__cta font-mono">
                    {product.btnText || "View tool"} <i className="fas fa-arrow-right" aria-hidden="true" />
                  </span>
                </div>
              </SmartLink>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function UpdatesSection({ section, updates, loading, error }) {
  if (!loading && !error && !updates.length) return null;
  return (
    <section className="home-section home-updates">
      <div className="container home-updates__layout">
        <div className="home-updates__intro">
          <SectionHeading section={section} />
          <Link to="/updates" className="btn btn-secondary">All updates</Link>
        </div>
        <div>
          <SectionStatus loading={loading} error={error} label="updates" />
          {!loading && !error && <UpdatesTimeline updates={updates} />}
        </div>
      </div>
    </section>
  );
}

function VideoPlayer({ video }) {
  const [playing, setPlaying] = useState(false);
  const id = video.id.videoId;
  if (playing) {
    return (
      <div className="home-video-feature__frame">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={video.snippet.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <button type="button" className="home-video-feature__frame home-video-feature__poster" onClick={() => setPlaying(true)}>
      <img src={video.snippet.thumbnails.high.url} alt="" />
      <span className="home-video-feature__play" aria-hidden="true"><i className="fas fa-play" /></span>
      <span className="visually-hidden">Play {video.snippet.title}</span>
    </button>
  );
}

export function VideosSection({ section, featured, videos, loading, error }) {
  const lead = featured || videos[0];
  const list = videos.filter((video) => video !== lead).slice(0, Math.max(0, (section.count || 5) - 1));
  return (
    <section className="home-section home-videos">
      <div className="container">
        <SectionHeading section={section} action={<Link to="/videos" className="btn btn-secondary">All videos</Link>} />
        <SectionStatus loading={loading} error={error} empty={!lead} label="videos" />
        {lead && (
          <div className="home-videos__layout">
            <div className="home-video-feature">
              <VideoPlayer key={lead.id.videoId} video={lead} />
              <div className="home-video-feature__info">
                <span className="font-mono">
                  {featured ? "Featured" : "Newest upload"} · {new Date(lead.snippet.publishedAt).toLocaleDateString()}
                </span>
                <h3>{lead.snippet.title}</h3>
              </div>
            </div>
            {list.length > 0 && (
              <ul className="home-video-list">
                {list.map((video) => (
                  <li key={video.id.videoId}>
                    <a href={`https://www.youtube.com/watch?v=${video.id.videoId}`} target="_blank" rel="noopener noreferrer">
                      <img src={video.snippet.thumbnails.high.url} alt="" loading="lazy" />
                      <span>
                        <strong>{video.snippet.title}</strong>
                        <small className="font-mono">{new Date(video.snippet.publishedAt).toLocaleDateString()}</small>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

export function ReviewsSection({ section, reviews, loading }) {
  return (
    <section className="home-section latest-reviews">
      <div className="container">
        <SectionHeading section={section} action={<Link to="/reviews" className="btn btn-secondary">All reviews</Link>} />
        <SectionStatus loading={loading} empty={!reviews.length} label="reviews" />
        {!loading && reviews.length > 0 && (
          <div className="reviews-grid">
            {reviews.map((game) => (
              <div className="review-card" key={game.id}>
                <div className="review-poster">
                  <img src={game.poster} alt={game.title} loading="lazy" />
                  {game.rating != null && (
                    <div className="review-rating" style={{ borderColor: getRatingScoreColor(game.rating) }}>
                      <span className="rating-value" style={{ color: getRatingScoreColor(game.rating) }}>
                        {game.rating}
                      </span>
                      <span className="rating-max">/10</span>
                    </div>
                  )}
                </div>
                <div className="review-info">
                  <h3>{game.title}</h3>
                  <div className="review-meta">
                    <span className="review-year">{game.releaseYear}</span>
                    <span className="review-platform">{game.platform}</span>
                  </div>
                  <p className="review-excerpt">
                    {Array.isArray(game.review) && game.review[0] ? `${game.review[0].substring(0, 150)}...` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function CtaSection({ section }) {
  return (
    <section className="home-section home-cta">
      <div className="container">
        <div className="home-cta__panel">
          {section.eyebrow && <p className="animus-hero-id font-mono">{section.eyebrow}</p>}
          <h2>
            {section.heading} {section.highlight && <span className="highlight">{section.highlight}</span>}
          </h2>
          {section.intro && <p>{section.intro}</p>}
          <HomeButtons buttons={section.buttons} className="hero-buttons home-cta__buttons" />
        </div>
      </div>
    </section>
  );
}

export function CustomSection({ section }) {
  const hasHeading = section.eyebrow || section.heading || section.highlight || section.intro;
  if (!hasHeading && !(section.blocks || []).some((block) => !block.hidden)) return null;
  return (
    <section className="home-section home-custom">
      <div className="container">
        {hasHeading && <SectionHeading section={section} />}
        <PageBlocks blocks={section.blocks} />
      </div>
    </section>
  );
}
